import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, X, Save } from "lucide-react";
import { toast } from "sonner";
import { listServices, createService, updateService, deleteService, uploadMedia } from "@/lib/adminCmsApi";
import AdminShell from "@/components/admin/AdminShell";

const EMPTY = {
  slug: "", title: "", tag: "", short_description: "", image_url: "", image_public_id: "",
  icon_name: "Building2", order: 0, enabled: true,
  long_description: "", scope_items: [], frequency_options: [],
  seo_title: "", seo_description: "",
};

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setServices(await listServices()); }
    catch (e) { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      if (editing.id) {
        await updateService(editing.id, payload);
        toast.success("Saved");
      } else {
        await createService(payload);
        toast.success("Created");
      }
      setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete service "${s.title}"?`)) return;
    try { await deleteService(s.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const uploadImg = async (file) => {
    if (!file) return;
    try {
      const res = await uploadMedia(file, { folder: "ags/services", alt_text: editing.title });
      setEditing({ ...editing, image_url: res.url, image_public_id: res.public_id });
      toast.success("Image uploaded");
    } catch (e) { toast.error("Upload failed"); }
  };

  return (
    <AdminShell title="Services">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-ags-text-muted">Add, edit, reorder, or disable the services shown across the site.</p>
        <button onClick={() => setEditing({ ...EMPTY, order: services.length + 1 })}
                data-testid="new-service-btn"
                className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]">
          <Plus size={14} /> New Service
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-[#050B14]/60 z-50 flex items-start justify-center overflow-auto py-10 px-4">
          <div className="bg-white w-full max-w-3xl border border-[#0E2A57]/15 max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-ags-hairline flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg">{editing.id ? "Edit service" : "New service"}</h2>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4 flex-1">
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Title"><input className="inp" value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} /></Field>
                <Field label="Slug"><input className="inp font-mono text-sm" value={editing.slug} onChange={(e) => setEditing({...editing, slug: e.target.value})} /></Field>
                <Field label="Tag (e.g. '01 / Offices')"><input className="inp" value={editing.tag} onChange={(e) => setEditing({...editing, tag: e.target.value})} /></Field>
                <Field label="Icon (lucide name)"><input className="inp" value={editing.icon_name} onChange={(e) => setEditing({...editing, icon_name: e.target.value})} /></Field>
                <Field label="Order"><input type="number" className="inp" value={editing.order} onChange={(e) => setEditing({...editing, order: parseInt(e.target.value)||0})} /></Field>
                <Field label="Enabled">
                  <select className="inp" value={editing.enabled ? "1" : "0"} onChange={(e) => setEditing({...editing, enabled: e.target.value === "1"})}>
                    <option value="1">Visible</option><option value="0">Hidden</option>
                  </select>
                </Field>
              </div>
              <Field label="Short description (card)"><textarea rows={2} className="inp" value={editing.short_description} onChange={(e) => setEditing({...editing, short_description: e.target.value})} /></Field>
              <Field label="Long description (detail page)"><textarea rows={4} className="inp" value={editing.long_description} onChange={(e) => setEditing({...editing, long_description: e.target.value})} /></Field>
              <Field label="Cover Image">
                <div className="flex items-center gap-3">
                  {editing.image_url ? (
                    <img src={editing.image_url} alt="" className="w-32 h-20 object-cover border border-ags-hairline" />
                  ) : (
                    <div className="w-32 h-20 border-2 border-dashed border-[#0E2A57]/30 flex items-center justify-center text-ags-text-muted text-xs">No image</div>
                  )}
                  <label className="cursor-pointer inline-flex items-center gap-2 bg-white border border-[#0E2A57]/30 hover:bg-[#0E2A57] hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors">
                    Upload
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e.target.files?.[0])} />
                  </label>
                </div>
              </Field>
              <ListEditor label="Scope items (bullets shown on detail page)" items={editing.scope_items}
                          onChange={(v) => setEditing({...editing, scope_items: v})} />
              <ListEditor label="Frequency options (e.g. Daily, Weekly)" items={editing.frequency_options}
                          onChange={(v) => setEditing({...editing, frequency_options: v})} />
              <Field label="SEO Title"><input className="inp" value={editing.seo_title} onChange={(e) => setEditing({...editing, seo_title: e.target.value})} /></Field>
              <Field label="SEO Description"><textarea rows={2} className="inp" value={editing.seo_description} onChange={(e) => setEditing({...editing, seo_description: e.target.value})} /></Field>
            </div>
            <div className="px-6 py-4 border-t border-ags-hairline flex items-center gap-2 justify-end">
              <button onClick={() => setEditing(null)} className="px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] border border-[#0E2A57]/30">Cancel</button>
              <button onClick={save} data-testid="save-service" className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-5 py-2 font-mono text-[10px] uppercase tracking-[0.22em]"><Save size={12} /> Save</button>
            </div>
          </div>
          <style>{`.inp{width:100%;background:white;border:1px solid rgba(14,42,87,0.15);padding:8px 12px;outline:none}.inp:focus{border-color:#0E2A57}`}</style>
        </div>
      )}

      <div className="bg-white border border-[#0E2A57]/15">
        {loading ? <div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin" /></div> :
          <ul className="divide-y divide-ags-hairline">
            {services.map((s) => (
              <li key={s.id} data-testid={`service-row-${s.slug}`} className="px-6 py-4 flex items-center gap-4 hover:bg-ags-cream">
                {s.image_url ? <img src={s.image_url} alt="" className="w-16 h-12 object-cover border border-ags-hairline" /> : <div className="w-16 h-12 bg-ags-cream" />}
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] tracking-[0.18em] text-[#2BB6E5]">{s.tag} · order {s.order}{!s.enabled && " · HIDDEN"}</div>
                  <div className="font-heading font-bold text-ags-ink tracking-[-0.02em]">{s.title}</div>
                  <div className="font-mono text-xs text-ags-text-muted mt-1">/{s.slug}</div>
                </div>
                <button onClick={() => setEditing({...s})} data-testid={`edit-service-${s.slug}`} className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em]"><Pencil size={12} /> Edit</button>
                <button onClick={() => remove(s)} className="text-red-600 hover:bg-red-50 p-2"><Trash2 size={16} /></button>
              </li>
            ))}
          </ul>
        }
      </div>
    </AdminShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function ListEditor({ label, items, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted">{label}</span>
        <button type="button" onClick={() => onChange([...(items||[]), ""])} className="inline-flex items-center gap-1 bg-[#0E2A57] text-white px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em]">
          <Plus size={10} /> Add
        </button>
      </div>
      <div className="space-y-2">
        {(items || []).map((it, i) => (
          <div key={i} className="flex gap-2">
            <input className="inp text-sm" value={it} onChange={(e) => { const n=[...items]; n[i]=e.target.value; onChange(n); }} />
            <button type="button" onClick={() => { const n=[...items]; n.splice(i,1); onChange(n); }} className="px-2 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
